const CENTS_PER_UNIT = 100;
const CENT_DECIMALS = 2;

/**
 * Convierte unidades de divisa (lo que teclea el usuario: `12,30`) a céntimos.
 *
 * El doble paso es intencionado. `units * 100` arrastra error de IEEE 754
 * (`12.30 * 100` es `1229.9999999999998`), y redondear directamente falla en el
 * medio céntimo: `1.005 * 100` es `100.49999999999999`, que redondearía a 100 en
 * lugar de a 101. Normalizar con `toFixed` antes de redondear resuelve ambos.
 */
export function unitsToCents(units: number): number {
  const normalized = Number((units * CENTS_PER_UNIT).toFixed(CENT_DECIMALS));

  return Math.round(normalized);
}

/** Convierte céntimos al valor decimal que espera un input numérico. */
export function centsToUnits(cents: number): number {
  return cents / CENTS_PER_UNIT;
}

/**
 * Interpreta lo que el usuario escribe en el campo de importe.
 *
 * El campo es de texto y no numérico porque el diseño muestra el importe con
 * formato español (`12,30`), que un `input[type=number]` no puede representar:
 * pintaría `12.3`. Se aceptan coma y punto como separador decimal, y se toleran
 * separadores de millar y el símbolo de divisa por si se pega un valor copiado.
 *
 * @returns céntimos, o `null` si no se puede interpretar.
 */
export function parseAmountInput(value: string): number | null {
  const cleaned = value
    .trim()
    // El símbolo sólo se admite al principio o al final: `12€34` es basura,
    // no mil doscientos treinta y cuatro.
    .replace(/^[€£$]\s*/, '')
    .replace(/\s*[€£$]$/, '')
    .replace(/\s/g, '')
    // Miles con punto y decimales con coma: 1.234,56 → 1234.56
    .replace(/\.(?=\d{3}(\D|$))/g, '')
    .replace(',', '.');

  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return null;
  }

  return unitsToCents(Number(cleaned));
}

/**
 * Formatea céntimos para el campo de importe: siempre dos decimales y coma como
 * separador, de modo que junto al símbolo fijo del campo se lea `€12,30`.
 */
export function formatAmountInput(cents: number): string {
  return centsToUnits(cents).toFixed(CENT_DECIMALS).replace('.', ',');
}
